
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Alert, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
    IconButton, Collapse, Chip
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import { useCompetence } from '../contexts/CompetenceContext';
import axios from '../api/api';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Row Component for Drill-down
const Row = ({ row, lineName, rank }) => {
    const [open, setOpen] = useState(false);

    const activeCenters = row.centros.filter(c => c.receita !== 0 || c.custo !== 0);
    activeCenters.sort((a, b) => b.receita - a.receita);

    // Rank badge color
    const getRankColor = (rank) => {
        if (rank === 1) return 'success';
        if (rank === 2) return 'info';
        if (rank === 3) return 'warning';
        return 'default';
    };

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} hover>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip label={`#${rank}`} size="small" color={getRankColor(rank)} />
                        <Typography variant="body2" fontWeight="bold">{lineName}</Typography>
                    </Box>
                </TableCell>
                <TableCell align="right">{row.receita.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                <TableCell align="right">{(row.custo_direto || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                <TableCell align="right" sx={{ color: (row.margem_contribuicao || 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                    {(row.margem_contribuicao || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2" sx={{ color: (row.margem_contribuicao_pct || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {(row.margem_contribuicao_pct || 0).toFixed(1)}%
                    </Typography>
                </TableCell>
                <TableCell align="right">{(row.custo_alocado || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                <TableCell align="right" sx={{ color: row.lucro >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                    {row.lucro.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2" sx={{ color: row.margem >= 0 ? 'success.main' : 'error.main' }}>
                        {row.margem.toFixed(1)}%
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="caption" color="textSecondary">
                        {activeCenters.length} CCs
                    </Typography>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Detalhamento por Centro de Custo
                            </Typography>
                            <Table size="small" aria-label="cost centers">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Centro de Custo</TableCell>
                                        <TableCell align="right">Receita</TableCell>
                                        <TableCell align="right">Custo</TableCell>
                                        <TableCell align="right">Resultado</TableCell>
                                        <TableCell align="right">Margem %</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeCenters.map((cc) => (
                                        <TableRow key={cc.nome}>
                                            <TableCell component="th" scope="row">
                                                {cc.nome}
                                            </TableCell>
                                            <TableCell align="right">{cc.receita.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                                            <TableCell align="right">{cc.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                                            <TableCell align="right" sx={{ color: cc.lucro >= 0 ? 'success.main' : 'error.main' }}>
                                                {cc.lucro.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="caption" sx={{ color: cc.margem >= 0 ? 'success.main' : 'error.main' }}>
                                                    {cc.margem.toFixed(1)}%
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {activeCenters.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                Nenhum centro de custo com movimentação.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const ProfitabilityPage = () => {
    const { globalCompetence } = useCompetence();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (globalCompetence) {
            loadData(globalCompetence);
        }
    }, [globalCompetence]);

    const loadData = async (comp) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/api/analytics/profitability/${comp}`);
            setData(response.data);
        } catch (err) {
            console.error('Erro ao carregar lucratividade:', err);
            const errorMessage = err.response?.data?.error || 'Erro ao carregar relatório de lucratividade.';
            setError(errorMessage);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    if (!globalCompetence) {
        return (
            <Box p={3}>
                <Alert severity="info">Selecione uma competência no menu lateral para visualizar o relatório.</Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box p={3} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!data || Object.keys(data).length === 0) {
        return (
            <Box p={3}>
                <Alert severity="warning">Nenhum dado encontrado para esta competência.</Alert>
            </Box>
        );
    }

    // Check if API returned an error
    if (data.error) {
        return (
            <Box p={3}>
                <Alert severity="error">{data.error}</Alert>
            </Box>
        );
    }

    // Validate that data has the expected structure (object with service line keys)
    const hasValidData = Object.keys(data).some(key => {
        const item = data[key];
        return item && typeof item === 'object' && ('receita' in item || 'custo' in item);
    });

    if (!hasValidData) {
        return (
            <Box p={3}>
                <Alert severity="warning">
                    Nenhuma linha de serviço configurada ou nenhum dado disponível para esta competência.
                    <br />
                    Configure as linhas de serviço em <strong>Dados Mestres → Linhas de Serviço</strong>.
                </Alert>
            </Box>
        );
    }

    // Prepare Chart Data and Rankings
    const labels = Object.keys(data).filter(key => key !== 'Não Mapeado' || (data['Não Mapeado']?.receita > 0 || data['Não Mapeado']?.custo > 0));

    // Sort by Contribution Margin descending for ranking
    labels.sort((a, b) => (data[b].margem_contribuicao || 0) - (data[a].margem_contribuicao || 0));

    const revenueData = labels.map(l => data[l].receita);
    const directCostData = labels.map(l => data[l].custo_direto || 0);
    const allocatedCostData = labels.map(l => data[l].custo_alocado || 0);
    const contributionMarginData = labels.map(l => data[l].margem_contribuicao || 0);

    const chartData = {
        labels,
        datasets: [
            {
                label: 'Receita',
                data: revenueData,
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
            },
            {
                label: 'Custo Direto',
                data: directCostData,
                backgroundColor: 'rgba(255, 159, 64, 0.6)',
            },
            {
                label: 'Custo Alocado',
                data: allocatedCostData,
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
            },
            {
                label: 'Margem de Contribuição',
                data: contributionMarginData,
                backgroundColor: 'rgba(53, 162, 235, 0.6)',
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Análise de Lucratividade e Margem de Contribuição' },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                }
            }
        }
    };

    // Calculate Totals
    const totalRevenue = Object.values(data).reduce((acc, curr) => acc + curr.receita, 0);
    const totalDirectCost = Object.values(data).reduce((acc, curr) => acc + (curr.custo_direto || 0), 0);
    const totalAllocatedCost = Object.values(data).reduce((acc, curr) => acc + (curr.custo_alocado || 0), 0);
    const totalContributionMargin = totalRevenue - totalDirectCost;
    const totalProfit = totalRevenue - totalDirectCost - totalAllocatedCost;
    const totalMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    const totalContributionMarginPct = totalRevenue > 0 ? (totalContributionMargin / totalRevenue) * 100 : 0;

    return (
        <Box p={3}>
            <Box mb={3}>
                <Typography variant="h4" gutterBottom>
                    Análise de Lucratividade - {globalCompetence}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Visão consolidada de receitas, custos diretos, custos alocados e margem de contribuição por Linha de Serviço.
                </Typography>
            </Box>

            {/* KPI Cards */}
            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">Receita Total</Typography>
                            <Typography variant="h6" color="primary">
                                {totalRevenue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">Custo Direto</Typography>
                            <Typography variant="h6" color="warning.main">
                                {totalDirectCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">Margem de Contribuição</Typography>
                            <Typography variant="h6" sx={{ color: totalContributionMargin >= 0 ? 'success.main' : 'error.main' }}>
                                {totalContributionMargin.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {totalContributionMarginPct.toFixed(1)}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">Custo Alocado</Typography>
                            <Typography variant="h6" color="error">
                                {totalAllocatedCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">Resultado Final</Typography>
                            <Typography variant="h6" sx={{ color: totalProfit >= 0 ? 'success.main' : 'error.main' }}>
                                {totalProfit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {totalMargin.toFixed(1)}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Chart */}
            <Paper sx={{ p: 2, mb: 4 }}>
                <Bar options={options} data={chartData} height={80} />
            </Paper>

            {/* Detailed Table */}
            <TableContainer component={Paper}>
                <Table aria-label="profitability table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Linha de Serviço</TableCell>
                            <TableCell align="right">Receita</TableCell>
                            <TableCell align="right">Custo Direto</TableCell>
                            <TableCell align="right">Margem Contrib.</TableCell>
                            <TableCell align="right">MC %</TableCell>
                            <TableCell align="right">Custo Alocado</TableCell>
                            <TableCell align="right">Resultado</TableCell>
                            <TableCell align="right">Margem %</TableCell>
                            <TableCell align="right">CCs</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {labels.map((line, index) => (
                            <Row key={line} row={data[line]} lineName={line} rank={index + 1} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ProfitabilityPage;
